import * as React from 'react';

import { BodySection } from './BodySection';
import { DataGrid } from './DataGrid';
import { PageIndexes } from './Paging';

import { Dictionary } from './Utils';
import { GridSelection } from './GridSelection';

export class Body extends React.Component<Body.Props> {

  public static displayName: string = 'DataGridBody';

  // used to exclude scrollbar width from body width calculations
  private clientWidth: number = 0;

  // used to exclude scrollbar height from body height calculations
  private clientHeight: number = 0;

  private scrollLeft: number = 0;
  private scrollTop: number = 0;
  private scrollable: DataGrid.IScrollable | null = null;

  private handleScrollableRefUpdated = (s: DataGrid.IScrollable | null) => {
    this.scrollable = s;
    this.updateClientWidthAndHeight();
    if (this.props.onIScrollableRefUpdate) {
      this.props.onIScrollableRefUpdate(s);
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: Body.Props): void {
    if (nextProps.rememberLastScrollPos) {
      if (!this.isScrollTopPosValidForNextProps(nextProps)) {
        this.scrollToTop();
      }
    }
  }

  public componentDidUpdate(): void {
    this.updateClientWidthAndHeight();

    const scrollPos = this.getScrollPosition();
    if (this.scrollLeft !== scrollPos.scrollLeft || this.scrollTop !== scrollPos.scrollTop ||
       !this.isScrollTopPosValidForPageIndexes(scrollPos.scrollTop, this.props.pageIndexes)
    ) {
      this.onScroll(scrollPos.scrollLeft, scrollPos.scrollTop);
    }
  }

  private updateClientWidthAndHeight(): void {
    if (!this.scrollable) { return; }

    const scrollNode = this.getScrollNode();
    if (scrollNode) {
      this.clientHeight = scrollNode.clientHeight;
      this.clientWidth = scrollNode.clientWidth;
    }
  }

  private getScrollNode(): Element | null {
    if (!this.scrollable) { return null; }
    return this.scrollable.getScrollContainer();
  }

  private getScrollPosition = (): Body.ScrollPosition => {
    if (!this.scrollable) {
      return { scrollLeft: this.scrollLeft, scrollTop: this.scrollTop };
    }
    return this.scrollable.getScrollPosition();
  }

  public render(): JSX.Element {
    const rows = this.props.rows;
    const scrollComponentProps: DataGrid.ScrollComponentProps = {
      marginTop: this.props.headerHeight,
      rootCssClass: 'tb-react-dg-body',
      scrollingRootId: this.props.scrollingRootId,
      onScroll: this.onScroll,
      onRefUpdated: this.handleScrollableRefUpdated
    };

    // tslint:disable-next-line:no-any
    let sections: any;
    let sectionsStyle: React.CSSProperties;

    const actualTotalColumnWidth = this.props.totalColumnWidth + (this.props.leftMargin || 0);

    if (!rows.length) {
      sections = this.props.emptyChild;
      sectionsStyle = {
        width: (actualTotalColumnWidth > this.clientWidth) ? actualTotalColumnWidth : '100%',
        height: '100%'
      };
    } else {
      sections = this.renderSections();
      const topSpacer = this.props.pageIndexes.startRowIdx * this.props.rowOuterHeight;
      sectionsStyle = {
        paddingTop: topSpacer,
        paddingLeft: this.props.leftSpacer,
        height: rows.length * this.props.rowOuterHeight - topSpacer,
        width: (actualTotalColumnWidth > this.clientWidth) ? (actualTotalColumnWidth - this.props.leftSpacer) : '100%'
      };
    }

    return React.createElement(
      this.props.scrollComponent,
      scrollComponentProps,
      (
        <div style={sectionsStyle}>
          {sections}
        </div>
      )
    );
  }

  public onRowStoreChanged(): void {
    // e.g. after filtering to an empty list. Skip scrollToTop; re-init.
    if (!this.props.rememberLastScrollPos) {
      this.scrollToTop();
    }
  }

  private scrollToTop(): void {
    if (!this.scrollable) {
      return;
    }

    window.setTimeout(() => {
      if (!!this.scrollable) {
        this.scrollable.setScrollPosition(null, 0);
      }
    });
  }

  // tslint:disable-next-line:no-any
  private renderSections(): React.ReactElement<any>[] {
    const pageIndexes = this.props.pageIndexes;

    // tslint:disable-next-line:no-any
    let sections: React.ReactElement<any>[] = [];

    for (let sectionRowStartIdx = pageIndexes.startRowIdx; sectionRowStartIdx <= pageIndexes.endRowIdx; sectionRowStartIdx += this.props.sectionRows) {
      sections.push(<BodySection {...{
        key: Math.floor(sectionRowStartIdx / this.props.sectionRows),
        cols: this.props.cols,
        colStartIdx: pageIndexes.startColIdx,
        colEndIdx: Math.min(pageIndexes.endColIdx, this.props.cols.length - 1),
        columnWidths: this.props.columnWidths,
        leftMargin: this.props.leftMargin,
        measuredColumnWidths: this.props.measuredColumnWidths,
        measureColumnWidths: sectionRowStartIdx === pageIndexes.startRowIdx ? this.props.measureColumnWidths : undefined,
        rows: this.props.rows,
        rowStartIdx: sectionRowStartIdx,
        rowEndIdx: Math.min(sectionRowStartIdx + this.props.sectionRows, this.props.rows.length) - 1,
        onCellClick: this.props.onCellClick,
        onCellDoubleClick: this.props.onCellDoubleClick,
        onCellContextMenu: this.props.onCellContextMenu,
        onRowHeaderCellClick: this.props.onRowHeaderCellClick,
        selectionModel: this.props.selectionModel,
        selectionTimestamp: this.props.selectionTimestamp,
        showRowBanding: this.props.showRowBanding,
        showHorizontalGridLines: this.props.showHorizontalGridLines,
        showVerticalGridLines: this.props.showVerticalGridLines,
        rowOuterHeight: this.props.rowOuterHeight
      }} />);
    }
    return sections;
  }

  private onScroll = (left: number, top: number) => {
    this.scrollLeft = left;
    this.scrollTop = top;
    this.props.onBodyScroll(left, top);
  }

  private isScrollTopPosValidForNextProps(nextProps: Body.Props): boolean {
    const nextRowCount = nextProps.rows.length;
    const nextRowOuterHeight = nextProps.rowOuterHeight;

    if (nextRowCount === this.props.rows.length && nextRowOuterHeight === this.props.rowOuterHeight) {
      return true;
    }

    return this.scrollTop <= (nextRowCount * nextRowOuterHeight);
  }

  private isScrollTopPosValidForPageIndexes(scrollTop: number, pageIndexes: PageIndexes) {
    const firstRowTopPx = pageIndexes.startRowIdx * this.props.rowOuterHeight;
    let lastRowBottomPx = (pageIndexes.endRowIdx + 1) * this.props.rowOuterHeight;

    const rowsInViewPort = Math.floor(this.clientHeight / this.props.rowOuterHeight);
    const rowsCount = this.props.rows.length;
    if (rowsCount > rowsInViewPort && (pageIndexes.endRowIdx + 1) < rowsCount) {
      lastRowBottomPx -= (rowsInViewPort * this.props.rowOuterHeight);
    }

    return scrollTop >= Math.max(firstRowTopPx, 0) && scrollTop <= Math.max(lastRowBottomPx, 0);
  }
}

export namespace Body {

  export interface ScrollPosition {
    scrollLeft: number;
    scrollTop: number;
  };

  export interface Props {
    /**
     * Specified header height to adjust top positioning if needed
     */
    headerHeight: number;

    showRowBanding?: boolean;
    showHorizontalGridLines?: boolean;
    showVerticalGridLines?: boolean;
    cols: DataGrid.Column[];
    columnWidths?: DataGrid.ColumnWidths;

    totalColumnWidth: number;

    leftMargin?: number;
    leftSpacer: number;

    measuredColumnWidths: Dictionary<number>;

    // tslint:disable-next-line:no-any
    measureColumnWidths?: (colWidths: Dictionary<number>) => any;

    /**
     * Callback for providing scroll updates.
     */
    onBodyScroll: (left: number, top: number) => void;
    rows: DataGrid.Row[];
    emptyChild?: React.ReactChild;

    selectionModel?: GridSelection;
    selectionTimestamp?: number;

    onCellClick?: DataGrid.ClickHandler;
    onCellDoubleClick?: DataGrid.ClickHandler;
    onCellContextMenu?: DataGrid.ClickHandler;

    onRowHeaderCellClick?: (rowIdx: number, e: React.MouseEvent<HTMLDivElement>) => void;

    scrollComponent: React.ComponentClass<DataGrid.ScrollComponentProps>;
    scrollingRootId?: string;
    onIScrollableRefUpdate?: (scrollable: DataGrid.IScrollable | null) => void;
    rowOuterHeight: number;
    colsPerChunk: number;
    sectionRows: number;
    rememberLastScrollPos: boolean;
    pageIndexes: PageIndexes;
  }
}
